export const CASES_ID_SEARCH_DEBOUNCE_MS = 300;

export const CASES_ID_TYPE_STORAGE_KEY = 'casesIdType';

export const CASES_ID_TYPES = ['case', 'external'] as const;

export type CasesIdType = (typeof CASES_ID_TYPES)[number];

export const isNumericCaseId = (value: string): boolean => /^\d+$/.test(value);

export const isCasesIdType = (value: string | null | undefined): value is CasesIdType =>
    value === 'case' || value === 'external';

export const readStoredCasesIdType = (
    storage: Pick<Storage, 'getItem'> = localStorage
): CasesIdType => {
    const stored = storage.getItem(CASES_ID_TYPE_STORAGE_KEY);
    return isCasesIdType(stored) ? stored : 'case';
};

export const writeStoredCasesIdType = (
    value: CasesIdType,
    storage: Pick<Storage, 'setItem'> = localStorage
): void => {
    storage.setItem(CASES_ID_TYPE_STORAGE_KEY, value);
};

export const casesIdTypeLocaleKey = (idType: CasesIdType): 'case#' | 'externalId' =>
    idType === 'external' ? 'externalId' : 'case#';

export const filterColumnOptions = <T extends { label: string }>(
    options: T[],
    query: string
): T[] => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) {
        return options;
    }
    return options.filter((option) => option.label.toLowerCase().includes(normalized));
};

/** Table filters send null when cleared; empty string drops the GET /cases query param. */
export const toCasesQueryFilter = (value: string | null | undefined): string => value || '';

/**
 * Id query values stay strings. Omit empty and "0".
 * Do not Number() — the list filter is case_id or external_id, not the PK path.
 */
export const toCasesQueryCaseId = (value: string | null | undefined): string => {
    const trimmed = value?.trim() ?? '';
    if (!trimmed || trimmed === '0') {
        return '';
    }
    return trimmed;
};

export const optionalCaseIdentifier = (value: string | number | null | undefined): string => {
    if (value === null || value === undefined || value === '') {
        return '';
    }
    return String(value);
};

export const caseIdDisplayValue = (
    row: { internal_id?: string | number | null; external_id?: string | null },
    idType: CasesIdType
): string =>
    idType === 'external'
        ? optionalCaseIdentifier(row.external_id)
        : optionalCaseIdentifier(row.internal_id);

/** Table Case ID filter wins; otherwise a numeric dashboard search is treated as case_id. */
export const resolveCasesQueryCaseId = (
    tableCaseId: string | null | undefined,
    searchInput: string
): string => {
    const fromTable = toCasesQueryCaseId(tableCaseId);
    if (fromTable) {
        return fromTable;
    }
    const search = searchInput.trim();
    return isNumericCaseId(search) ? toCasesQueryCaseId(search) : '';
};

/** Send only the param for the selected switch: case_id (internal) or external_id. */
export const resolveCasesIdQuery = (
    tableId: string | null | undefined,
    searchInput: string,
    idType: CasesIdType
): { case_id: string; external_id: string } => {
    if (idType === 'external') {
        return { case_id: '', external_id: toCasesQueryCaseId(tableId) };
    }
    return { case_id: resolveCasesQueryCaseId(tableId, searchInput), external_id: '' };
};
